import { Box } from 'designSystem/components/Box'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'designSystem/components/Pagination'
import React, { useMemo } from 'react'
import styled from 'styled-components'

type PageItem =
  | { kind: 'page'; index: number }
  | { kind: 'ellipsis'; key: string }

const MAX_VISIBLE = 7

const FooterStatus = styled.p`
  flex-shrink: 0;
  margin: 0;
  white-space: nowrap;
  color: var(--muted-foreground);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
`

function getVisiblePages(currentPage: number, totalPages: number): PageItem[] {
  const page = (index: number): PageItem => ({ kind: 'page', index })
  const ellipsis = (key: string): PageItem => ({ kind: 'ellipsis', key })

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => page(i))
  }

  if (currentPage <= 3) {
    return [
      page(0),
      page(1),
      page(2),
      page(3),
      page(4),
      ellipsis('end'),
      page(totalPages - 1),
    ]
  }

  if (currentPage >= totalPages - 4) {
    return [
      page(0),
      ellipsis('start'),
      page(totalPages - 5),
      page(totalPages - 4),
      page(totalPages - 3),
      page(totalPages - 2),
      page(totalPages - 1),
    ]
  }

  return [
    page(0),
    ellipsis('start'),
    page(currentPage - 1),
    page(currentPage),
    page(currentPage + 1),
    ellipsis('end'),
    page(totalPages - 1),
  ]
}

interface PaginationControlsProps {
  currentPage: number
  resultCountLabel?: React.ReactNode
  rightContent?: React.ReactNode
  totalPages: number
  onPageChange: (page: number) => void
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  resultCountLabel,
  rightContent,
  totalPages,
  onPageChange,
}) => {
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  )

  const showPagination = totalPages > 1

  if (!resultCountLabel && !rightContent && !showPagination) return null

  const previousDisabled = currentPage === 0
  const nextDisabled = currentPage === totalPages - 1

  const getDisabledLinkProps = (disabled: boolean) => ({
    'aria-disabled': disabled || undefined,
    tabIndex: disabled ? -1 : undefined,
  })

  const handlePageLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    page: number,
    disabled = false
  ) => {
    event.preventDefault()
    if (disabled) return
    onPageChange(page)
  }

  return (
    <Box
      display='flex'
      align='center'
      justify='between'
      gap='sm'
      width='full'
      shrink={false}
      overflow='xAuto'
      border='top'
      padding='sm'>
      {(resultCountLabel || rightContent) && (
        <Box display='flex' align='center' gap='sm' shrink={false}>
          {rightContent}
          {resultCountLabel && <FooterStatus>{resultCountLabel}</FooterStatus>}
        </Box>
      )}
      {showPagination && (
        <Box display='flex' align='center' justify='end' shrink={false}>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href='#'
                  size='lg'
                  {...getDisabledLinkProps(previousDisabled)}
                  onClick={(event) =>
                    handlePageLinkClick(
                      event,
                      currentPage - 1,
                      previousDisabled
                    )
                  }
                />
              </PaginationItem>
              {visiblePages.map((item) =>
                item.kind === 'page' ? (
                  <PaginationItem key={item.index}>
                    <PaginationLink
                      href='#'
                      aria-label={`Go to page ${item.index + 1}`}
                      isActive={item.index === currentPage}
                      size='icon-lg'
                      onClick={(event) =>
                        handlePageLinkClick(event, item.index)
                      }>
                      {item.index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item.key}>
                    <PaginationEllipsis />
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  href='#'
                  size='lg'
                  {...getDisabledLinkProps(nextDisabled)}
                  onClick={(event) =>
                    handlePageLinkClick(event, currentPage + 1, nextDisabled)
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </Box>
      )}
    </Box>
  )
}
